import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Text } from "components/atoms/text";
import {
  MIA_ACCENT,
  StatusDoughnut,
  StatusSlice,
  TimelineBar,
} from "components/molecules/period-charts";
import { useActAs } from "contexts/ActAsContext";
import { statsApi } from "core/api";
import {
  PeriodStats,
  PeriodStatsParams,
  STATS_PERIODS,
  StatsLanguage,
  StatsPeriod,
} from "core/types/stats.types";
import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const ALL_CLIENTS = "all";

// i18next per default fa l'escape HTML dei valori interpolati ("/" -> "&#x2F;"): qui il
// testo finisce in nodi React, che fanno gia' l'escape, quindi va disattivato per non
// mostrare le entita' (es. "18&#x2F;10&#x2F;2026"). Locale: la config globale resta com'e'.
const NO_ESCAPE = { interpolation: { escapeValue: false } } as const;

// Righe visibili nelle classifiche prima di "Mostra tutti" (il backend ne manda fino a 50).
const TOP_VISIBLE = 10;

type SortDir = "asc" | "desc";
type SortValue = string | number;
type Column<R> = {
  key: string;
  label: ReactNode;
  numeric?: boolean;
  value: (row: R) => SortValue;
  render?: (row: R) => ReactNode;
  cellSx?: object;
};

const compareValues = (a: SortValue, b: SortValue) =>
  typeof a === "number" && typeof b === "number"
    ? a - b
    : String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });

// Tabella ordinabile: click sull'intestazione = ordina (secondo click inverte). L'ordinamento
// e' stabile, quindi a parita' vale l'ordine del backend. Con `limit` mostra le prime righe
// (dopo l'ordinamento) e un pulsante per vederle tutte.
function SortableTable<R>({
  rows,
  columns,
  rowKey,
  defaultSort,
  defaultDir = "desc",
  limit,
  maxHeight,
  emptyLabel,
  onRowClick,
}: {
  rows: R[];
  columns: Column<R>[];
  rowKey: (row: R) => string;
  defaultSort: string;
  defaultDir?: SortDir;
  limit?: number;
  maxHeight: number;
  emptyLabel: string;
  onRowClick?: (row: R) => void;
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState(defaultSort);
  const [dir, setDir] = useState<SortDir>(defaultDir);
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return rows;
    const sign = dir === "asc" ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index }))
      .sort(
        (a, b) => sign * compareValues(col.value(a.row), col.value(b.row)) || a.index - b.index
      )
      .map((x) => x.row);
  }, [rows, columns, sortKey, dir]);

  const canCollapse = !!limit && rows.length > limit;
  const visible = canCollapse && !showAll ? sorted.slice(0, limit) : sorted;

  const onSort = (col: Column<R>) => {
    if (col.key === sortKey) {
      setDir(dir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(col.key);
      // Numeri: prima i piu' alti; testo: dalla A alla Z.
      setDir(col.numeric ? "desc" : "asc");
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.numeric ? "right" : "left"}
                  sortDirection={sortKey === col.key ? dir : false}
                >
                  <TableSortLabel
                    active={sortKey === col.key}
                    direction={sortKey === col.key ? dir : col.numeric ? "desc" : "asc"}
                    onClick={() => onSort(col)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>{emptyLabel}</TableCell>
              </TableRow>
            ) : (
              visible.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover={!!onRowClick}
                  sx={onRowClick ? { cursor: "pointer" } : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.numeric ? "right" : "left"}
                      sx={col.cellSx}
                    >
                      {col.render ? col.render(row) : col.value(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {canCollapse && (
        <Box display="flex" justifyContent="center" py={1}>
          <Button size="small" onClick={() => setShowAll((v) => !v)}>
            {showAll
              ? t("dashboard.period.show_less")
              : t("dashboard.period.show_all", { count: rows.length, ...NO_ESCAPE })}
          </Button>
        </Box>
      )}
    </>
  );
}

// Colonne comuni alle classifiche (siti e clienti): nel periodo segnalati, rimossi e non
// piu' nei motori; "in rimozione" e' la situazione alla data del resoconto.
type RankRow = {
  signaled: number;
  neutralized: number;
  removed?: number;
  deindexed?: number;
  in_removal: number;
  unverifiable?: number;
  not_removed?: number;
};

// Scope scelto dall'operatore: tutti, un intestatario di pratiche (customer_name: le pratiche
// aperte dall'operatore per conto del cliente) o un account cliente (proprietario).
type ClientKind = "all" | "customer" | "account";
type ClientOption = {
  key: string;
  kind: ClientKind;
  value: string;
  label: string;
  group?: string;
  hint?: string;
};

// Gli intestatari si confrontano come fa il backend (spazi ai lati e maiuscole ignorati):
// "Alessio Romeo" dalla classifica e "alessio romeo" dalla lista sono la stessa opzione.
const optionKey = (kind: ClientKind, value: string) => {
  if (kind === "all") return ALL_CLIENTS;
  return kind === "customer"
    ? `customer:${value.trim().toLocaleLowerCase()}`
    : `account:${value}`;
};

interface Props {
  // Operatore = admin/legals (anche durante una delega: /account non e' delegabile e il
  // ruolo resta quello dell'operatore).
  isOperator: boolean;
}

// Riquadro numerico compatto: la spiegazione lunga va in `info` (tooltip), non sotto il numero,
// altrimenti un solo riquadro con tre righe di testo allunga tutta la fila.
const KpiTile: FunctionComponent<{
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  info?: string;
  accent?: boolean;
  small?: boolean;
}> = ({ label, value, hint, info, accent, small }) => (
  <Card sx={{ height: "100%", px: 2.5, py: 2 }}>
    <Box display="flex" alignItems="flex-start" gap={0.5}>
      <Text secondary fontSize={"13px"} fontWeight={"600"}>
        {label}
      </Text>
      {info && (
        <Tooltip title={info} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 15, mt: "2px", color: "text.secondary", cursor: "help" }} />
        </Tooltip>
      )}
    </Box>
    <Text
      fontSize={small ? "22px" : "28px"}
      fontWeight={"700"}
      sx={accent ? { color: `${MIA_ACCENT} !important` } : undefined}
    >
      {value}
    </Text>
    {hint && (
      <Text secondary fontSize={"12px"}>
        {hint}
      </Text>
    )}
  </Card>
);

const SectionTitle: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => (
  <Text fontSize={"16px"} fontWeight={"700"} paddingY={"15px"} paddingLeft={"8px"}>
    {children}
  </Text>
);

const PeriodDashboard: FunctionComponent<Props> = ({ isOperator }) => {
  const { t, i18n } = useTranslation();
  const { session: actAsSession } = useActAs();

  // In delega lo scope e' il cliente delegato: niente selettore, il backend scopa da solo
  // la GET (X-Act-As); il PDF invece parte come operatore, quindi con user_id esplicito.
  const isActingAs = !!actAsSession;
  const canChooseClient = isOperator && !isActingAs;
  const canDownload = isOperator;

  const [period, setPeriod] = useState<StatsPeriod>("30d");
  const [clientKey, setClientKey] = useState<string>(ALL_CLIENTS);
  const [lang, setLang] = useState<StatsLanguage>(
    (i18n.language || "it").startsWith("en") ? "en" : "it"
  );
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [isClientsLoading, setIsClientsLoading] = useState(false);

  const [stats, setStats] = useState<PeriodStats>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>();

  const clientOptions: ClientOption[] = useMemo(
    () => [
      {
        key: ALL_CLIENTS,
        kind: "all" as ClientKind,
        value: ALL_CLIENTS,
        label: t("dashboard.period.all_clients"),
      },
      ...clients,
    ],
    [clients, t]
  );
  const selectedClient =
    clientOptions.find((o) => o.key === clientKey) ?? clientOptions[0];

  const params: PeriodStatsParams = useMemo(() => {
    if (canChooseClient) {
      if (selectedClient.kind === "customer")
        return { period, customer: selectedClient.value };
      if (selectedClient.kind === "account")
        return { period, userId: selectedClient.value };
      return { period, userId: ALL_CLIENTS };
    }
    if (isActingAs && actAsSession) return { period, userId: actAsSession.subject.id };
    return { period };
  }, [period, selectedClient, canChooseClient, isActingAs, actAsSession]);

  useEffect(() => {
    if (!canChooseClient) return;
    let cancelled = false;
    setIsClientsLoading(true);
    const customersGroup = t("dashboard.period.group_customers");
    const accountsGroup = t("dashboard.period.group_accounts");
    statsApi
      .getClients()
      .then(({ customers, accounts }) => {
        if (cancelled) return;
        // Intestatari prima (sono il caso normale), poi gli account; ognuno in ordine
        // alfabetico. L'Autocomplete con groupBy vuole le opzioni gia' ordinate per gruppo.
        const byLabel = (a: ClientOption, b: ClientOption) =>
          a.label.localeCompare(b.label);
        setClients([
          ...(customers || [])
            .map(
              (c): ClientOption => ({
                key: optionKey("customer", c.name),
                kind: "customer",
                value: c.name,
                label: c.name,
                group: customersGroup,
                hint: t("dashboard.period.customer_practices", {
                  count: c.practices,
                  ...NO_ESCAPE,
                }),
              })
            )
            .sort(byLabel),
          ...(accounts || [])
            .map(
              (a): ClientOption => ({
                key: optionKey("account", a.user_id),
                kind: "account",
                value: a.user_id,
                label: a.email && a.email !== a.name ? `${a.name} — ${a.email}` : a.name,
                group: accountsGroup,
              })
            )
            .sort(byLabel),
        ]);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (!cancelled) setIsClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canChooseClient, t]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);
    // In delega la GET viaggia con X-Act-As: user_id non serve (e non va mandato, lo
    // scope lo decide il backend).
    const request: PeriodStatsParams = isActingAs ? { period } : params;
    statsApi
      .getPeriod(request)
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setStats(undefined);
          setError(t("dashboard.period.load_error"));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params, isActingAs, period, t]);

  // Click sulla classifica: l'intestatario se la riga ne ha uno, altrimenti l'account.
  const selectClient = (kind: "customer" | "account", value: string, name: string) => {
    const key = optionKey(kind, value);
    if (!clients.some((c) => c.key === key)) {
      // Cliente presente nella classifica ma non (ancora) nella lista: lo si aggiunge
      // per non lasciare l'Autocomplete con un valore senza opzione.
      setClients((prev) => [
        ...prev,
        {
          key,
          kind,
          value,
          label: name,
          group: t(
            kind === "customer"
              ? "dashboard.period.group_customers"
              : "dashboard.period.group_accounts"
          ),
        },
      ]);
    }
    setClientKey(key);
  };

  const handleDownload = async () => {
    setDownloadError(undefined);
    setIsDownloading(true);
    try {
      const { data, filename } = await statsApi.downloadPeriodReport(
        params,
        lang
      );
      if (!data || !data.byteLength) {
        setDownloadError(t("dashboard.period.download_error"));
        return;
      }
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Il nome giusto (Resoconto_<cliente>_<periodo>_<data>.pdf) lo decide il backend;
      // il fallback non usa mai l'id Keycloak del cliente.
      const who = (stats?.scope?.label || selectedClient.label || "")
        .normalize("NFKD")
        .replace(/[^A-Za-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      const day = new Date().toISOString().slice(0, 10);
      const fallback = `Resoconto_${who ? `${who}_` : ""}${period}_${day}.pdf`;
      link.setAttribute("download", filename || fallback);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setDownloadError(t("dashboard.period.download_error"));
    } finally {
      setIsDownloading(false);
    }
  };

  const locale = lang === "en" ? "en-GB" : "it-IT";
  const formatDate = (value?: string) => {
    if (!value) return "";
    const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  const formatBucket = (start: string) => {
    const date = new Date(`${start}T00:00:00`);
    if (Number.isNaN(date.getTime())) return start;
    return stats?.bucket === "month"
      ? date.toLocaleDateString(locale, { month: "short", year: "2-digit" })
      : date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
  };

  const totals = stats?.totals;
  const snapshot = stats?.status_snapshot;
  // Vuoto = nessun contenuto per cui sia mai partita una richiesta fino a fine periodo. Con
  // contenuti segnalati prima del periodo si mostrano comunque risultati e situazione.
  const isEmpty = !!totals && totals.signaled_total_to_date === 0;

  // Ciambella = fotografia di tutti i contenuti segnalati fino alla data del resoconto.
  const slices: StatusSlice[] = snapshot
    ? (
        [
          ["removed", snapshot.removed],
          ["deindexed", snapshot.deindexed],
          ["removed_declared", snapshot.removed_declared],
          ["in_removal", snapshot.in_removal],
          ["unverifiable", snapshot.unverifiable ?? 0],
          ["rejected", snapshot.rejected],
          ["not_removed", snapshot.not_removed ?? 0],
        ] as [string, number][]
      )
        .filter(
          ([key, value]) =>
            !["rejected", "removed_declared", "unverifiable", "not_removed"].includes(
              key
            ) || value > 0
        )
        .map(([key, value]) => ({
          key,
          value,
          label: t(`dashboard.period.status.${key}`),
        }))
    : [];

  const pct = (value?: number) => `${Math.round((value || 0) * 100)}%`;
  const rate = totals ? pct(totals.neutralized_rate) : "";
  const unverifiable = totals?.unverifiable ?? 0;
  const rejected = totals?.rejected ?? 0;
  const notRemoved = totals?.not_removed ?? 0;
  // Numeri di lavoro dell'operatore: il backend li manda solo a lui (non al cliente, ne' in
  // delega). Seconda cintura qui: in delega l'operatore vede quello che vede il cliente.
  const showOperatorFigures = isOperator && !isActingAs;
  const rateVerifiable =
    showOperatorFigures &&
    unverifiable > 0 &&
    totals?.neutralized_rate_verifiable !== undefined
      ? pct(totals.neutralized_rate_verifiable)
      : undefined;
  const withoutRequest = showOperatorFigures
    ? totals?.neutralized_without_request ?? 0
    : 0;
  const avgDays =
    totals?.avg_days_to_neutralize === null ||
    totals?.avg_days_to_neutralize === undefined
      ? "—"
      : totals.avg_days_to_neutralize.toLocaleString(locale, {
          maximumFractionDigits: 1,
        });

  // Rimossi / non piu' nei motori separati (al posto del vecchio "Risultati"); con un
  // backend che non li manda ancora si ripiega sul totale nella colonna "Rimossi".
  const rankColumns: Column<RankRow>[] = [
    {
      key: "signaled",
      label: t("dashboard.period.kpi.signaled"),
      numeric: true,
      value: (r) => r.signaled,
    },
    {
      key: "removed",
      label: t("dashboard.period.kpi.col_removed"),
      numeric: true,
      value: (r) => r.removed ?? r.neutralized,
    },
    {
      key: "deindexed",
      label: t("dashboard.period.kpi.col_deindexed"),
      numeric: true,
      value: (r) => r.deindexed ?? 0,
    },
    {
      key: "in_removal",
      label: t("dashboard.period.kpi.in_removal"),
      numeric: true,
      value: (r) => r.in_removal,
    },
    // Solo se ce n'e' almeno uno: per la maggior parte dei clienti sarebbe una colonna di zeri.
    ...(unverifiable > 0
      ? [
          {
            key: "unverifiable",
            label: t("dashboard.period.kpi.col_unverifiable"),
            numeric: true,
            value: (r: RankRow) => r.unverifiable ?? 0,
          },
        ]
      : []),
    ...(notRemoved > 0
      ? [
          {
            key: "not_removed",
            label: t("dashboard.period.kpi.col_not_removed"),
            numeric: true,
            value: (r: RankRow) => r.not_removed ?? 0,
          },
        ]
      : []),
  ];
  const showAllHeight = 480;

  const showTopClients =
    canChooseClient &&
    selectedClient.kind === "all" &&
    !!stats?.top_clients?.length;

  return (
    <Grid container spacing={3}>
      {/* Barra filtri */}
      <Grid item xs={12}>
        <Card sx={{ p: 4 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
          >
            <Box>
              <Text fontSize={"18px"} fontWeight={"700"}>
                {t("dashboard.period.title")}
              </Text>
              {stats && (
                <Text secondary fontSize={"13px"}>
                  {t("dashboard.period.range", {
                    from: formatDate(stats.from),
                    to: formatDate(stats.to),
                    ...NO_ESCAPE,
                  })}
                  {stats.scope?.label ? ` · ${stats.scope.label}` : ""}
                </Text>
              )}
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              useFlexGap
              flexWrap="wrap"
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={period}
                onChange={(_e, v: StatsPeriod | null) => {
                  if (v) setPeriod(v);
                }}
                sx={{ flexWrap: "wrap" }}
              >
                {STATS_PERIODS.map((p) => (
                  <ToggleButton key={p} value={p}>
                    {t(`dashboard.period.periods.${p}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {canChooseClient && (
                <Autocomplete
                  id="autocomplete-period-client"
                  size="small"
                  sx={{ minWidth: { xs: "100%", sm: 260 } }}
                  value={selectedClient}
                  options={clientOptions}
                  loading={isClientsLoading}
                  disableClearable
                  isOptionEqualToValue={(o, v) => o.key === v.key}
                  getOptionLabel={(o) => o.label}
                  groupBy={(o) => o.group ?? ""}
                  renderGroup={(groupParams) => (
                    <li key={groupParams.key}>
                      {groupParams.group && (
                        <ListSubheader component="div">{groupParams.group}</ListSubheader>
                      )}
                      <ul style={{ padding: 0 }}>{groupParams.children}</ul>
                    </li>
                  )}
                  renderOption={(optionProps, o) => (
                    <li {...optionProps} key={o.key}>
                      <Box>
                        <Text fontSize={"14px"}>{o.label}</Text>
                        {o.hint && (
                          <Text secondary fontSize={"12px"}>
                            {o.hint}
                          </Text>
                        )}
                      </Box>
                    </li>
                  )}
                  onChange={(_e, v) => setClientKey(v?.key ?? ALL_CLIENTS)}
                  renderInput={(inputProps) => (
                    <TextField
                      {...inputProps}
                      label={t("dashboard.period.client_label")}
                    />
                  )}
                />
              )}
              {canDownload && (
                <>
                  <TextField
                    select
                    size="small"
                    label={t("dashboard.period.language_label")}
                    value={lang}
                    onChange={(e) => setLang(e.target.value as StatsLanguage)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="it">{t("language.italian")}</MenuItem>
                    <MenuItem value="en">{t("language.english")}</MenuItem>
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    startIcon={
                      isDownloading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DownloadRoundedIcon />
                      )
                    }
                  >
                    {t("dashboard.period.download_pdf")}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
          {downloadError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {downloadError}
            </Alert>
          )}
        </Card>
      </Grid>

      {isLoading && !stats && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress color="secondary" size={"60px"} />
          </Box>
        </Grid>
      )}

      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      {stats && totals && (
        <Grid
          item
          xs={12}
          container
          spacing={3}
          sx={{ opacity: isLoading ? 0.5 : 1, transition: "opacity .2s" }}
        >
          {isEmpty ? (
            <Grid item xs={12}>
              <Card sx={{ p: 6, textAlign: "center" }}>
                <Text fontSize={"16px"} fontWeight={"700"}>
                  {t("dashboard.period.empty_title")}
                </Text>
                <Text secondary fontSize={"13px"} mt={1}>
                  {t("dashboard.period.empty_description")}
                </Text>
              </Card>
            </Grid>
          ) : (
            <>
              {/* Esito: una fila sola, riquadri della stessa misura */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiTile label={t("dashboard.period.kpi.signaled")} value={totals.signaled} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiTile
                      label={t("dashboard.period.kpi.removed")}
                      value={totals.removed}
                      hint={
                        totals.removed_declared > 0
                          ? t("dashboard.period.kpi.removed_declared", {
                              count: totals.removed_declared,
                              ...NO_ESCAPE,
                            })
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiTile label={t("dashboard.period.kpi.deindexed")} value={totals.deindexed} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiTile
                      label={t("dashboard.period.kpi.in_removal")}
                      value={totals.in_removal}
                      hint={t("dashboard.period.kpi.as_of", {
                        date: formatDate(stats.to),
                        ...NO_ESCAPE,
                      })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiTile
                      label={t("dashboard.period.kpi.neutralized_rate")}
                      value={rate}
                      info={t("dashboard.period.kpi.neutralized_rate_hint")}
                      hint={
                        rateVerifiable
                          ? t("dashboard.period.kpi.neutralized_rate_verifiable", {
                              rate: rateVerifiable,
                              ...NO_ESCAPE,
                            })
                          : undefined
                      }
                      accent
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KpiTile label={t("dashboard.period.kpi.avg_days")} value={avgDays} />
                  </Grid>
                </Grid>
              </Grid>

              {/* Fuori dal conteggio "in rimozione": solo le voci presenti */}
              {(unverifiable > 0 || rejected > 0 || notRemoved > 0) && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {unverifiable > 0 && (
                      <Grid item xs={12} sm={4}>
                        <KpiTile
                          small
                          label={t("dashboard.period.kpi.unverifiable")}
                          value={unverifiable}
                          info={t("dashboard.period.kpi.unverifiable_hint")}
                        />
                      </Grid>
                    )}
                    {rejected > 0 && (
                      <Grid item xs={12} sm={4}>
                        <KpiTile
                          small
                          label={t("dashboard.period.kpi.rejected")}
                          value={rejected}
                          info={t("dashboard.period.kpi.rejected_hint")}
                        />
                      </Grid>
                    )}
                    {notRemoved > 0 && (
                      <Grid item xs={12} sm={4}>
                        <KpiTile
                          small
                          label={t("dashboard.period.kpi.not_removed")}
                          value={notRemoved}
                          info={t("dashboard.period.kpi.not_removed_hint")}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
              {withoutRequest > 0 && (
                <Grid item xs={12} sx={{ pt: "8px !important" }}>
                  <Text secondary fontSize={"12px"} paddingLeft={"8px"}>
                    {t("dashboard.period.kpi.without_request", {
                      count: withoutRequest,
                      ...NO_ESCAPE,
                    })}
                  </Text>
                </Grid>
              )}
            </>
          )}

          {/* Attivita' svolte nel periodo: hanno senso anche con zero segnalati. Fila propria,
              cosi' non si allungano all'altezza dei grafici */}
          <Grid item xs={12}>
            <SectionTitle>{t("dashboard.period.activity_title")}</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md>
                <KpiTile small label={t("dashboard.period.activity.notices_sent")} value={totals.notices_sent} />
              </Grid>
              <Grid item xs={6} sm={4} md>
                <KpiTile small label={t("dashboard.period.activity.reminders_sent")} value={totals.reminders_sent} />
              </Grid>
              <Grid item xs={6} sm={4} md>
                <KpiTile
                  small
                  label={t("dashboard.period.activity.deindex_requests")}
                  value={totals.deindex_requests}
                  hint={
                    totals.deindex_requests_urls > 0
                      ? t("dashboard.period.activity.deindex_requests_urls", {
                          count: totals.deindex_requests_urls,
                          ...NO_ESCAPE,
                        })
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={6} sm={6} md>
                <KpiTile small label={t("dashboard.period.activity.practices_opened")} value={totals.practices_opened} />
              </Grid>
              <Grid item xs={12} sm={6} md>
                <KpiTile small label={t("dashboard.period.activity.searches")} value={totals.searches} />
              </Grid>
            </Grid>
          </Grid>

          {!isEmpty && (
            <>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%", p: 4 }}>
                  <SectionTitle>
                    {t("dashboard.period.status_title", {
                      date: formatDate(stats.to),
                      ...NO_ESCAPE,
                    })}
                  </SectionTitle>
                  <StatusDoughnut
                    slices={slices}
                    centerValue={rate}
                    centerLabel={t("dashboard.period.status_center")}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Card sx={{ height: "100%", p: 4 }}>
                  <SectionTitle>{t("dashboard.period.timeline_title")}</SectionTitle>
                  <TimelineBar
                    labels={stats.timeline.map((b) => formatBucket(b.start))}
                    signaled={stats.timeline.map((b) => b.signaled)}
                    neutralized={stats.timeline.map((b) => b.neutralized)}
                    signaledLabel={t("dashboard.period.timeline_signaled")}
                    neutralizedLabel={t("dashboard.period.timeline_neutralized")}
                  />
                </Card>
              </Grid>

              <Grid item xs={12} md={showTopClients ? 6 : 12}>
                <Card sx={{ height: "100%" }}>
                  <SectionTitle>{t("dashboard.period.top_domains")}</SectionTitle>
                  <SortableTable
                    rows={stats.top_domains}
                    columns={[
                      {
                        key: "domain",
                        label: t("dashboard.source"),
                        value: (d) => d.domain,
                        cellSx: { wordBreak: "break-all" },
                      },
                      ...rankColumns,
                    ]}
                    rowKey={(d) => d.domain}
                    defaultSort="signaled"
                    limit={TOP_VISIBLE}
                    maxHeight={showAllHeight}
                    emptyLabel={t("dashboard.period.no_rows")}
                  />
                </Card>
              </Grid>

              {showTopClients && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <SectionTitle>{t("dashboard.period.top_clients")}</SectionTitle>
                    <SortableTable
                      rows={stats.top_clients!}
                      columns={[
                        {
                          key: "name",
                          label: t("dashboard.period.client_label"),
                          value: (c) => c.name,
                        },
                        ...rankColumns,
                      ]}
                      rowKey={(c) => (c.customer ? `c:${c.customer}` : `u:${c.user_id}`)}
                      defaultSort="signaled"
                      limit={TOP_VISIBLE}
                      maxHeight={showAllHeight}
                      emptyLabel={t("dashboard.period.no_rows")}
                      onRowClick={(c) => {
                        if (c.customer) selectClient("customer", c.customer, c.name);
                        else if (c.user_id) selectClient("account", c.user_id, c.name);
                      }}
                    />
                  </Card>
                </Grid>
              )}

              {stats.recent_neutralized.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <SectionTitle>{t("dashboard.period.recent_neutralized")}</SectionTitle>
                    <SortableTable
                      rows={stats.recent_neutralized}
                      columns={[
                        {
                          key: "date",
                          label: t("dashboard.period.date"),
                          value: (r) => r.date,
                          render: (r) => formatDate(r.date),
                          cellSx: { whiteSpace: "nowrap" },
                        },
                        {
                          key: "domain",
                          label: t("dashboard.source"),
                          value: (r) => r.domain,
                        },
                        {
                          key: "content",
                          label: t("dashboard.period.content"),
                          value: (r) => r.title || r.url,
                          render: (r) => (
                            <a href={r.url} target="_blank" rel="noopener noreferrer">
                              {r.title || r.url}
                            </a>
                          ),
                          cellSx: { wordBreak: "break-all" },
                        },
                        {
                          key: "outcome",
                          label: t("dashboard.period.outcome"),
                          value: (r) =>
                            t(`dashboard.period.status.${r.status}`, {
                              defaultValue: r.status,
                            }),
                        },
                      ]}
                      rowKey={(r) => `${r.url}-${r.date}`}
                      defaultSort="date"
                      maxHeight={320}
                      emptyLabel={t("dashboard.period.no_rows")}
                    />
                  </Card>
                </Grid>
              )}
            </>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default PeriodDashboard;
